import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Plug, Printer, WifiHigh, Desktop, GearSix, Question, CheckCircle, XCircle } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const CONTENT = {
  en: {
    page_title: "Tether Photography",
    page_sub:
      "Shooting tethered means your camera streams every photo directly to a computer or tablet as you click. Common in studio, product, fashion, and classroom teaching — clients see results instantly, focus is verified at full size, and reshoots happen in seconds.",
    download: "Download as PDF",
    generating: "Generating...",
    overview_title: "What tethered shooting is and when to use it",
    overview_intro:
      "A USB cable (or Wi-Fi link) connects your camera to a computer running tether software. Each shutter press transfers the RAW or JPEG to the computer in 2-8 seconds, where it appears full-size on the monitor and is automatically saved to your editing folder.",
    pros_title: "Pros",
    pros: [
      "Verify focus at 100% on a big screen — never miss critical sharpness",
      "Clients and art directors see images instantly and approve the look on set",
      "Live histogram and exposure feedback at editing-monitor accuracy",
      "RAW files land in your Lightroom/Capture One folder ready to grade as you shoot",
      "Classroom teaching: students see exactly what the instructor's camera saw, in real time",
      "Backup happens automatically — even if a card corrupts, the computer file is safe",
    ],
    cons_title: "Cons",
    cons: [
      "Requires a cable or stable Wi-Fi — limits how far you can move from the computer",
      "Cables fail if not Tether Tools-grade or strain-relieved",
      "Battery drains faster (camera is constantly transferring)",
      "Adds a piece of gear to manage on location",
      "Slower than card-only shooting for fast-action work (sports, weddings)",
    ],
    when_use_title: "When tether is essential",
    when_use_points: [
      "Studio product photography — color critical, focus critical, art director on set",
      "Fashion / portraits with a creative director or client present",
      "Catalog and e-commerce — speed matters but precision matters more",
      "Architectural and real-estate stills — verify level, parallax, perspective",
      "Classroom demonstrations — every student sees the result of each adjustment",
      "Any work where client sign-off in the moment saves a reshoot",
    ],
    software_title: "Software comparison",
    software_intro:
      "Each major brand has its own free tether software, plus universal options. Pick by camera brand and editing workflow.",
    software_table: [
      {
        name: "Adobe Lightroom Classic — Tether",
        platforms: "Windows + Mac",
        cost: "Included with Lightroom subscription",
        cameras: "Canon (most), Nikon (most), Leica SL, Some Sony (limited)",
        notes: "Files import straight into Lightroom catalog with develop presets applied. Best workflow integration. No live view in older versions.",
      },
      {
        name: "Capture One Pro — the studio standard",
        platforms: "Windows + Mac",
        cost: "$24/mo or one-time license",
        cameras: "Phase One, Sony, Fuji, Canon, Nikon, Leica",
        notes: "Industry standard for studio. Live view, overlays, focus check, HDR/focus stacking. Highest tether speeds. Steeper learning curve.",
      },
      {
        name: "Canon EOS Utility",
        platforms: "Windows + Mac",
        cost: "Free with Canon cameras",
        cameras: "Canon DSLR / mirrorless only",
        notes: "Free first-party. Solid live view, remote control, full settings panel. Saves to folder — pair with Lightroom auto-import for full workflow.",
      },
      {
        name: "Nikon NX Tether",
        platforms: "Windows + Mac",
        cost: "Free with Nikon cameras",
        cameras: "Nikon Z series + most recent DSLRs",
        notes: "Replaces older Camera Control Pro 2 (paid). Free, basic live view, full remote settings. Pairs with Lightroom auto-import folder.",
      },
      {
        name: "Sony Imaging Edge Desktop",
        platforms: "Windows + Mac",
        cost: "Free with Sony cameras",
        cameras: "Sony Alpha mirrorless",
        notes: "Three apps in one (Remote, Viewer, Edit). Live view + remote control. Decent for free; limited compared to Capture One.",
      },
      {
        name: "Fujifilm X Acquire",
        platforms: "Windows + Mac",
        cost: "Free with Fuji cameras",
        cameras: "Fuji X series + GFX",
        notes: "Free, simple file transfer + remote settings. Most Fuji studio shooters use Capture One Express (free for Fuji) for full features.",
      },
      {
        name: "Smart Shooter 5",
        platforms: "Windows + Mac",
        cost: "$200 one-time",
        cameras: "Canon, Nikon, Sony",
        notes: "Specialized for product photography automation, scripting, multi-camera. Niche but very powerful.",
      },
    ],
    cables_title: "Cables — quality matters more than you think",
    cables_intro:
      "A cheap cable will work for 30 minutes then start dropping connections, corrupting transfers, and embarrassing you in front of clients. Buy proper studio-grade cables — they pay for themselves in one shoot.",
    cables_points: [
      "USB 3.0 / 3.1 (blue tip): minimum for modern RAW transfers. USB 2.0 (black tip) works but adds 2-3 seconds per file.",
      "USB-C to USB-C: standard for new cameras (Sony A7 IV+, Canon R5, Nikon Z9, Fuji X-H2)",
      "USB-C to USB-A: needed if your computer is older — common laptop side",
      "Mini-B / Micro-B: legacy — older Canon DSLRs (5D Mark III), older Nikon",
      "Length: 15 ft (4.5m) is the practical limit before signal loss. For longer runs use a USB extension hub or Tether Tools TetherBoost.",
      "Brand: Tether Tools is the gold standard ($25-$70 per cable). Their cables have screw-down JerkStopper strain relief. Cheap Amazon cables fail at the connector under tension.",
      "Color-coded JerkStopper accessories anchor the cable to the camera strap lug — prevent yanking the port off the camera body (a $400-1200 repair).",
    ],
    wireless_title: "Wireless tethering",
    wireless_intro:
      "Modern cameras can tether over Wi-Fi or even direct phone tethering. Slower than wired but frees you to move around. Best for events, weddings, and on-location lifestyle shoots.",
    wireless_points: [
      "Camera-to-phone apps: Canon Camera Connect, Nikon SnapBridge, Sony Imaging Edge Mobile, Fujifilm Camera Remote — all free, control your camera from your phone",
      "Camera-to-laptop Wi-Fi: most modern bodies have an FTP mode that uploads files to a folder over your studio network",
      "WiFi tether speed: 2-10 seconds per RAW (vs 1-3 for USB 3.0). Adequate for tethered review, slow for production.",
      "Tether Tools Air Direct ($300): a small dongle that creates its own dedicated Wi-Fi network for low-latency tethering — very popular with fashion shooters",
      "CamRanger 2 ($300): another popular wireless tether — works with Canon, Nikon, Fuji",
      "5GHz Wi-Fi is critical — 2.4GHz drops files in busy environments (weddings, studios with many devices)",
    ],
    setup_title: "Quick-start setup checklist",
    setup_steps: [
      "Plug the cable into the camera FIRST while it is off, then into the computer (avoids reverse current spike on the port)",
      "Turn the camera on. The computer should detect it within 5-10 seconds.",
      "Open your tether software. Select the camera from the device list.",
      "Set the import folder — make a new folder per shoot (e.g. /Shoots/2026-02-10-ClientName/)",
      "Set file naming convention (e.g. ClientName_001) before the first shutter press",
      "Apply a starter develop preset for instant client preview (Lightroom Develop preset on import)",
      "Take a test shot. Verify it appears on screen within 5 seconds and the histogram updates.",
      "Anchor the cable to your strap lug with a JerkStopper or velcro strap",
    ],
    troubleshoot_title: "Common issues and quick fixes",
    troubleshoot: [
      {
        p: "Camera not detected by tether software",
        a: "Power cycle the camera. Check USB cable is fully seated. Try a different USB port (avoid hubs for first connection). Verify the camera's USB mode is set to PC/Tether (not 'MTP' or 'Mass Storage').",
      },
      {
        p: "Files transfer slowly (10+ seconds)",
        a: "USB 2.0 cable on a USB 3.0 port — swap to a 3.0/3.1 cable (blue tip). Or RAW + large JPEG mode is enabled — switch to RAW only.",
      },
      {
        p: "Connection drops every 5-10 minutes",
        a: "Cheap cable failing under tension. Replace with Tether Tools or anchor with a JerkStopper. Disable USB selective suspend in Windows power settings.",
      },
      {
        p: "Computer crashes / freezes mid-shoot",
        a: "Editing software has too much going on. Close all other apps, disable cloud sync (Dropbox, OneDrive) on the import folder, and reduce Lightroom preview quality.",
      },
      {
        p: "Files appear corrupted or won't open",
        a: "USB cable is failing. Replace immediately. Some bodies also need their firmware updated for newer software (check manufacturer site).",
      },
      {
        p: "Live view is laggy or low resolution",
        a: "Normal — most cameras send 1024x768 or 720p live view, not full 4K. Use Capture One for the highest live view quality currently available.",
      },
      {
        p: "Wi-Fi tether keeps disconnecting",
        a: "Switch to 5GHz network. Move closer to router. If using direct camera Wi-Fi, you may have moved out of range (~10m typical).",
      },
    ],
    footer_note:
      "For studio, product, and classroom work, tethered shooting changes the game. Spend the $50 on a quality Tether Tools cable, learn the software once, and you will never want to shoot to a card again for serious commercial work.",
  },
  es: {
    page_title: "Fotografia con Tether",
    page_sub:
      "Disparar con tether significa que tu camara envia cada foto directamente a una computadora o tablet en tiempo real. Comun en estudio, producto, moda y ensenanza — los clientes ven los resultados al instante, el enfoque se verifica a tamano completo, y las repeticiones ocurren en segundos.",
    download: "Descargar como PDF",
    generating: "Generando...",
    overview_title: "Que es y cuando usarlo",
    overview_intro:
      "Un cable USB (o Wi-Fi) conecta tu camara a una computadora con software de tether. Cada disparo transfiere el RAW o JPEG a la computadora en 2-8 segundos, donde aparece a tamano completo y se guarda automaticamente en tu carpeta de edicion.",
    pros_title: "Ventajas",
    pros: [
      "Verifica el enfoque al 100% en una pantalla grande — nunca pierdes nitidez critica",
      "Clientes y directores de arte ven las imagenes al instante y aprueban en el set",
      "Histograma y exposicion en vivo con la precision del monitor de edicion",
      "Los archivos RAW llegan listos a tu carpeta de Lightroom/Capture One para revelar mientras disparas",
      "Ensenanza en aula: los estudiantes ven exactamente lo que ve la camara del instructor en tiempo real",
      "Respaldo automatico — aunque la tarjeta se corrompa, el archivo en la computadora esta a salvo",
    ],
    cons_title: "Desventajas",
    cons: [
      "Requiere cable o Wi-Fi estable — limita la distancia respecto a la computadora",
      "Los cables fallan si no son de calidad Tether Tools o no tienen alivio de tension",
      "La bateria se descarga mas rapido (la camara transfiere constantemente)",
      "Agrega equipo extra que gestionar en locacion",
      "Mas lento que disparar a tarjeta para accion rapida (deportes, bodas)",
    ],
    when_use_title: "Cuando el tether es esencial",
    when_use_points: [
      "Producto en estudio — color y enfoque criticos, director de arte en el set",
      "Moda / retratos con un director creativo o cliente presente",
      "Catalogo y e-commerce — la velocidad importa pero la precision mas",
      "Arquitectura e inmobiliaria — verifica nivel, paralaje, perspectiva",
      "Demostraciones en aula — todos los estudiantes ven el resultado de cada ajuste",
      "Cualquier trabajo donde la aprobacion del cliente en el momento evita repetir la sesion",
    ],
    software_title: "Comparativa de software",
    software_intro:
      "Cada marca importante tiene su software de tether gratuito, mas opciones universales. Elige segun la marca de tu camara y tu flujo de edicion.",
    software_table: [
      {
        name: "Adobe Lightroom Classic — Tether",
        platforms: "Windows + Mac",
        cost: "Incluido con la suscripcion de Lightroom",
        cameras: "Canon (la mayoria), Nikon (la mayoria), Leica SL, Sony (limitado)",
        notes: "Los archivos llegan al catalogo de Lightroom con presets de revelado aplicados. Mejor integracion con el flujo de trabajo. Sin live view en versiones antiguas.",
      },
      {
        name: "Capture One Pro — el estandar de estudio",
        platforms: "Windows + Mac",
        cost: "$24/mes o licencia perpetua",
        cameras: "Phase One, Sony, Fuji, Canon, Nikon, Leica",
        notes: "Estandar de la industria en estudio. Live view, overlays, verificacion de enfoque, HDR/focus stacking. Maxima velocidad de tether. Curva de aprendizaje mas alta.",
      },
      {
        name: "Canon EOS Utility",
        platforms: "Windows + Mac",
        cost: "Gratis con camaras Canon",
        cameras: "Solo DSLR / mirrorless Canon",
        notes: "Gratuita de fabrica. Live view solido, control remoto, panel completo. Guarda en carpeta — combinar con auto-importacion de Lightroom.",
      },
      {
        name: "Nikon NX Tether",
        platforms: "Windows + Mac",
        cost: "Gratis con camaras Nikon",
        cameras: "Nikon serie Z + DSLR recientes",
        notes: "Reemplaza al antiguo Camera Control Pro 2 (de pago). Gratis, live view basico, control remoto completo. Combina con auto-importacion de Lightroom.",
      },
      {
        name: "Sony Imaging Edge Desktop",
        platforms: "Windows + Mac",
        cost: "Gratis con camaras Sony",
        cameras: "Sony Alpha mirrorless",
        notes: "Tres apps en una (Remote, Viewer, Edit). Live view + control remoto. Decente para gratis; limitado frente a Capture One.",
      },
      {
        name: "Fujifilm X Acquire",
        platforms: "Windows + Mac",
        cost: "Gratis con camaras Fuji",
        cameras: "Fuji serie X + GFX",
        notes: "Gratuita, transferencia simple + control remoto. La mayoria de Fuji usa Capture One Express (gratis para Fuji) para mas funciones.",
      },
      {
        name: "Smart Shooter 5",
        platforms: "Windows + Mac",
        cost: "$200 una sola vez",
        cameras: "Canon, Nikon, Sony",
        notes: "Especializado en automatizacion de fotografia de producto, scripts, multi-camara. Nicho pero muy potente.",
      },
    ],
    cables_title: "Cables — la calidad importa mas de lo que crees",
    cables_intro:
      "Un cable barato funcionara 30 minutos y luego empezara a perder conexion, corromper transferencias y avergonzarte ante el cliente. Compra cables de estudio — se pagan solos en una sola sesion.",
    cables_points: [
      "USB 3.0 / 3.1 (punta azul): minimo para RAW modernos. USB 2.0 (punta negra) funciona pero suma 2-3 segundos por archivo.",
      "USB-C a USB-C: estandar para camaras nuevas (Sony A7 IV+, Canon R5, Nikon Z9, Fuji X-H2)",
      "USB-C a USB-A: necesario si tu computadora es vieja — comun del lado del laptop",
      "Mini-B / Micro-B: legado — DSLR Canon antiguas (5D Mark III), Nikon antiguas",
      "Longitud: 4.5m es el limite practico antes de perdida de senal. Para mas largo usa hub USB o Tether Tools TetherBoost.",
      "Marca: Tether Tools es el estandar ($25-$70 por cable). Sus cables tienen alivio de tension JerkStopper. Los baratos de Amazon fallan en el conector bajo tension.",
      "Los accesorios JerkStopper de colores anclan el cable al lug de la correa — evitan arrancar el puerto del cuerpo de la camara (reparacion de $400-1200).",
    ],
    wireless_title: "Tether inalambrico",
    wireless_intro:
      "Las camaras modernas pueden transferir por Wi-Fi o directamente al telefono. Mas lento que cable pero te libera para moverte. Ideal para eventos, bodas y lifestyle en locacion.",
    wireless_points: [
      "Apps camara-a-telefono: Canon Camera Connect, Nikon SnapBridge, Sony Imaging Edge Mobile, Fujifilm Camera Remote — todas gratis, controla tu camara desde el telefono",
      "Wi-Fi camara-a-laptop: la mayoria de cuerpos modernos tienen modo FTP que sube archivos a una carpeta en tu red de estudio",
      "Velocidad WiFi: 2-10 segundos por RAW (vs 1-3 con USB 3.0). Adecuado para revision en vivo, lento para produccion.",
      "Tether Tools Air Direct ($300): un pequeno dongle que crea su propia red Wi-Fi dedicada para tether de baja latencia — muy popular con fotografos de moda",
      "CamRanger 2 ($300): otro tether inalambrico popular — funciona con Canon, Nikon, Fuji",
      "Wi-Fi de 5GHz es critico — el de 2.4GHz pierde archivos en entornos saturados (bodas, estudios con muchos dispositivos)",
    ],
    setup_title: "Lista de inicio rapido",
    setup_steps: [
      "Conecta el cable PRIMERO a la camara apagada, luego a la computadora (evita picos de corriente inversa en el puerto)",
      "Enciende la camara. La computadora deberia detectarla en 5-10 segundos.",
      "Abre tu software de tether. Selecciona la camara en la lista de dispositivos.",
      "Configura la carpeta de importacion — crea una carpeta nueva por sesion (ej. /Sesiones/2026-02-10-Cliente/)",
      "Configura el patron de nombres (ej. Cliente_001) antes del primer disparo",
      "Aplica un preset de revelado de inicio para vista previa instantanea al cliente (preset Develop de Lightroom al importar)",
      "Toma una foto de prueba. Verifica que aparezca en pantalla en 5 segundos y que el histograma se actualice.",
      "Ancla el cable al lug de la correa con un JerkStopper o velcro",
    ],
    troubleshoot_title: "Problemas comunes y soluciones rapidas",
    troubleshoot: [
      {
        p: "El software no detecta la camara",
        a: "Apaga y enciende la camara. Verifica que el cable USB este bien conectado. Prueba otro puerto USB (evita hubs en la primera conexion). Confirma que el modo USB de la camara este en PC/Tether (no 'MTP' ni 'Almacenamiento Masivo').",
      },
      {
        p: "Los archivos se transfieren lento (10+ segundos)",
        a: "Cable USB 2.0 en puerto USB 3.0 — cambia a cable 3.0/3.1 (punta azul). O RAW + JPEG grande activado — usa solo RAW.",
      },
      {
        p: "La conexion se cae cada 5-10 minutos",
        a: "Cable barato fallando bajo tension. Reemplaza con Tether Tools o ancla con JerkStopper. Desactiva 'suspension selectiva USB' en opciones de energia de Windows.",
      },
      {
        p: "La computadora se traba o congela durante la sesion",
        a: "El software de edicion tiene demasiado encima. Cierra todas las otras apps, desactiva sincronizacion de nube (Dropbox, OneDrive) en la carpeta, y reduce la calidad de previa de Lightroom.",
      },
      {
        p: "Los archivos llegan corruptos o no abren",
        a: "El cable USB esta fallando. Reemplaza ya. Algunas camaras tambien necesitan firmware actualizado para software nuevo (revisa la web del fabricante).",
      },
      {
        p: "El live view va lento o en baja resolucion",
        a: "Normal — la mayoria de camaras envian 1024x768 o 720p, no 4K. Capture One tiene el live view de mayor calidad disponible.",
      },
      {
        p: "El tether Wi-Fi se desconecta",
        a: "Cambia a red 5GHz. Acercate al router. Si usas Wi-Fi directo de la camara, podrias estar fuera de rango (~10m tipico).",
      },
    ],
    footer_note:
      "Para estudio, producto y aula, disparar con tether cambia el juego. Invierte $50 en un cable Tether Tools de calidad, aprende el software una vez, y no querras volver a disparar solo a tarjeta para trabajo comercial serio.",
  },
};

export default function TetherPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `tether-photography-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="tether-page"
    >
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="tether-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Overview */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Desktop weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.overview_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.overview_intro}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div className="bg-[#0A0A0A] border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  <p className="text-emerald-400 font-medium text-sm">{c.pros_title}</p>
                </div>
                <ul className="space-y-1.5">
                  {c.pros.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                      <span className="text-[#A1A1AA] text-xs leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0A0A0A] border border-rose-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle weight="fill" className="w-4 h-4 text-rose-400" />
                  <p className="text-rose-400 font-medium text-sm">{c.cons_title}</p>
                </div>
                <ul className="space-y-1.5">
                  {c.cons.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-rose-400 shrink-0" />
                      <span className="text-[#A1A1AA] text-xs leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* When to use */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">
              {c.when_use_title}
            </h2>
            <ul className="space-y-2">
              {c.when_use_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Software */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <GearSix weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.software_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.software_intro}</p>
            <div className="space-y-3">
              {c.software_table.map((sw, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4"
                  data-testid={`tether-software-${i}`}
                >
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{sw.name}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                    <div>
                      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold">Platforms</p>
                      <p className="text-[#D4D4D8] text-xs font-['JetBrains_Mono']">{sw.platforms}</p>
                    </div>
                    <div>
                      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold">Cost</p>
                      <p className="text-[#D4D4D8] text-xs font-['JetBrains_Mono']">{sw.cost}</p>
                    </div>
                    <div>
                      <p className="text-[#71717A] text-[10px] uppercase tracking-widest font-semibold">Cameras</p>
                      <p className="text-[#D4D4D8] text-xs">{sw.cameras}</p>
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed italic">{sw.notes}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Cables */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Plug weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.cables_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.cables_intro}</p>
            <ul className="space-y-2">
              {c.cables_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Wireless */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <WifiHigh weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.wireless_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.wireless_intro}</p>
            <ul className="space-y-2">
              {c.wireless_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Setup checklist */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">
              {c.setup_title}
            </h2>
            <ol className="space-y-2">
              {c.setup_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* Troubleshoot */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Question weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.troubleshoot_title}</h2>
            </div>
            <div className="space-y-3">
              {c.troubleshoot.map((t, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <p className="text-white text-sm font-medium mb-1">Q: {t.p}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">A: {t.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.p
            variants={itemAnim}
            className="text-[#A1A1AA] text-sm italic text-center max-w-3xl mx-auto leading-relaxed"
          >
            "{c.footer_note}"
          </motion.p>
        </motion.div>
      </div>

      {/* Print / PDF */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="tether-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.overview_title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.overview_intro}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-sm text-emerald-700 mb-1">{c.pros_title}</p>
              <ul className="list-disc ml-5 space-y-1">
                {c.pros.map((p, i) => (
                  <li key={i} className="text-xs text-gray-700">{p}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-sm text-rose-700 mb-1">{c.cons_title}</p>
              <ul className="list-disc ml-5 space-y-1">
                {c.cons.map((p, i) => (
                  <li key={i} className="text-xs text-gray-700">{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.when_use_title}</h2>
          <ul className="list-disc ml-5 space-y-1">
            {c.when_use_points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.software_title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.software_intro}</p>
          <table className="w-full text-xs border-collapse mb-2">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Software</th>
                <th className="border border-gray-300 p-2 text-left">OS</th>
                <th className="border border-gray-300 p-2 text-left">Cost</th>
                <th className="border border-gray-300 p-2 text-left">Cameras</th>
                <th className="border border-gray-300 p-2 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {c.software_table.map((sw, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-semibold">{sw.name}</td>
                  <td className="border border-gray-300 p-2 font-mono">{sw.platforms}</td>
                  <td className="border border-gray-300 p-2 font-mono">{sw.cost}</td>
                  <td className="border border-gray-300 p-2">{sw.cameras}</td>
                  <td className="border border-gray-300 p-2 italic text-gray-600">{sw.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.cables_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.cables_intro}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.cables_points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.wireless_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.wireless_intro}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.wireless_points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.setup_title}</h2>
          <ol className="list-decimal ml-5 space-y-1">
            {c.setup_steps.map((step, i) => (
              <li key={i} className="text-xs text-gray-700">{step}</li>
            ))}
          </ol>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.troubleshoot_title}</h2>
          {c.troubleshoot.map((t, i) => (
            <div key={i} className="mb-2">
              <p className="text-sm font-semibold">Q: {t.p}</p>
              <p className="text-xs text-gray-700">A: {t.a}</p>
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.footer_note}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
